import React, { useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { formatDateForDisplay } from "../../../utils/dateUtils";
import {
  getDetailPageMediaItems,
  getFundraiserMediaItems,
} from "../../../utils/fundraiserUtils";
import {
  shouldShowPendingBadge,
  shouldShowExpirationBadge,
  getPendingBadgeText,
  getExpirationBadgeText,
} from "../../../utils/badgesUtils";
import { FundraiserSelectDropdown } from "./FundraiserSelectDropdown";
import { fundraiserStore } from "../../../stores/FundraiserStore";
import { Fundraiser } from "../../../models/FundraisersGridModels";
import { CreateFundraiserModel, FundRaiserGridModel } from "../../../models";
import { NavigateFunction } from "react-router-dom";
import { withRouter } from "@mindmemobile/mm-shared-global";
import { FundRaiserStatus } from "../../../enums";

const DetailsContainer = styled.div`
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  border-radius: 20px;
  padding: 32px;
  margin-bottom: 40px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const DetailsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const DetailsHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: -5px;
  margin-bottom: 5px;
`;

const ClickableStatusBadge = styled.div<{ status: string }>`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  ${(props) => {
    switch (props.status) {
      case FundRaiserStatus.Active:
        return "background: #dcfce7; color: #166534; border: 2px solid #16a34a;";
      case FundRaiserStatus.Completed:
        return "background: #dbeafe; color: #1e40af; border: 2px solid #2563eb;";
      case FundRaiserStatus.Inactive:
        return "background: #fef3c7; color: #92400e; border: 2px solid #ca8a04;";
      case FundRaiserStatus.Draft:
        return "background: #e5e7eb; color: #374151; border: 2px solid #9ca3af;";
      default:
        return "background: #f3f4f6; color: #374151; border: 2px solid #9ca3af;";
    }
  }}
`;

const DetailsContent = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 32px 32px 40px 32px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  min-height: 320px;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 5px 400px 1fr;
  gap: 32px;
  align-items: center;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const DetailsLeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: center;
  align-items: flex-start;
  grid-column: 3 / 4;
  margin-left: 20px;
`;

const DetailsRightColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  grid-column: 2 / 3;
  height: 350px;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DetailLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DetailValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.4;

  /* For description fields, limit to 5 lines with ellipsis */
  &.description {
    display: -webkit-box;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.5;
    max-height: calc(1.5em * 5);
  }
`;

const DateRangeContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
`;

const PendingBadge = styled.div`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  background: #dbeafe;
  color: #1e40af;
  border: 1px solid #93c5fd;
  white-space: nowrap;
`;

const ExpirationBadge = styled.div`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  white-space: nowrap;
`;

const MediaGalleryContainer = styled.div`
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f5f9;
  position: relative;
  transition: all 0.2s ease;
  width: 100%;
  max-width: 400px;
`;

const MediaContainer = styled.div`
  position: relative;
  width: 100%;
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #f8fafc;
`;

const NavigationButton = styled.button<{ direction: "left" | "right" }>`
  position: absolute;
  top: 50%;
  ${(props) => props.direction}: 16px;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  opacity: 0;
  visibility: hidden;

  ${MediaContainer}:hover & {
    opacity: 1;
    visibility: visible;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.8);
    transform: translateY(-50%) scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }
`;

const MediaItem = styled.div<{ isActive: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: ${(props) => (props.isActive ? 1 : 0)};
  transition: opacity 0.5s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MediaImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
`;

const MediaVideo = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  object-fit: cover;
`;

const MediaIndicators = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 0 0 0;
  background: #ffffff;
`;

const Indicator = styled.button<{ isActive: boolean }>`
  width: ${(props) => (props.isActive ? "24px" : "10px")};
  height: 10px;
  border-radius: 5px;
  border: none;
  background: ${(props) => (props.isActive ? "#3b82f6" : "#cbd5e1")};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => (props.isActive ? "#2563eb" : "#94a3b8")};
  }
`;

const MediaInfo = styled.div`
  padding: 16px;
  background: #ffffff;
  border-top: none;
`;

const MediaTitle = styled.h4`
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;
  line-height: 1.3;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
  text-overflow: ellipsis;
  max-height: 3.6em;
`;

const MediaDescription = styled.p`
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
  text-overflow: ellipsis;
  max-height: 3em;
`;

interface FundraiserDetailsSectionProps {
  fundraiser: CreateFundraiserModel;
  onStatusClick: () => void;
  navigate: NavigateFunction;
}

const FundraiserDetailsSection: React.FC<FundraiserDetailsSectionProps> =
  observer(({ fundraiser, onStatusClick, navigate }) => {
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
    const [showFundraiserDropdown, setShowFundraiserDropdown] = useState(false);
    const [fundraiserSearchTerm, setFundraiserSearchTerm] = useState("");

    // Badge logic with priority (Pending takes priority over Expiration)
    const shouldShowPendingBadgeInDetails = shouldShowPendingBadge(
      fundraiser.startDate,
      fundraiser.status
    );
    const shouldShowExpirationBadgeInDetails =
      !shouldShowPendingBadgeInDetails &&
      shouldShowExpirationBadge(fundraiser.endDate, fundraiser.status);

    // Media gallery functionality
    const mediaItems = fundraiser.mediaItems; //getDetailPageMediaItems(fundraiser);

    const getVideoEmbedUrl = (url: string) => {
      if (url?.includes("youtube.com/watch?v=")) {
        const videoId = url.split("v=")[1]?.split("&")[0];
        return `https://www.youtube.com/embed/${videoId}`;
      }
      return url;
    };

    const currentMedia = mediaItems[currentMediaIndex] || mediaItems[0];

    // Media navigation functions
    const goToPrevious = () => {
      setCurrentMediaIndex((prev) =>
        prev === 0 ? mediaItems.length - 1 : prev - 1
      );
    };

    const goToNext = () => {
      setCurrentMediaIndex((prev) =>
        prev === mediaItems.length - 1 ? 0 : prev + 1
      );
    };

    const goToSlide = (index: number) => {
      setCurrentMediaIndex(index);
    };

    const getDateRangeDisplay = () => {
      const startDate = formatDateForDisplay(fundraiser.startDate);
      const endDate = fundraiser.endDate
        ? formatDateForDisplay(fundraiser.endDate)
        : null;

      if (endDate) {
        return `${startDate} - ${endDate}`;
      } else {
        return `${startDate} - Ongoing`;
      }
    };

    const handleFundraiserSelect = (selectedFundraiserId: string) => {
      fundraiserStore.setSelectedFundraiser(selectedFundraiserId);
      navigate("/fundraiser/fundraiser-details");
      setShowFundraiserDropdown(false);
      setFundraiserSearchTerm("");
    };

    return (
      <DetailsContainer>
        <DetailsHeader>
          <DetailsHeaderRow>
            <FundraiserSelectDropdown
              fundraiser={fundraiser}
              isOpen={showFundraiserDropdown}
              searchTerm={fundraiserSearchTerm}
              onToggle={() =>
                setShowFundraiserDropdown(!showFundraiserDropdown)
              }
              onSearchChange={setFundraiserSearchTerm}
              onSelect={handleFundraiserSelect}
            />
            <ClickableStatusBadge
              status={fundraiser.status}
              onClick={onStatusClick}
              title="Click to update status"
            >
              {fundraiser.status}
            </ClickableStatusBadge>
          </DetailsHeaderRow>
        </DetailsHeader>

        <DetailsContent>
          <DetailsGrid>
            {mediaItems.length > 0 && (
              <DetailsRightColumn>
                <MediaGalleryContainer>
                  <MediaContainer className="media-container">
                    {mediaItems.map((media, index) => (
                      <MediaItem
                        key={index}
                        isActive={index === currentMediaIndex}
                      >
                        {media.type === "Image" ? (
                          <MediaImage
                            src={media.fullURL}
                            alt={media.title || `Media ${index + 1}`}
                          />
                        ) : (
                          <MediaVideo
                            src={getVideoEmbedUrl(media.fullURL || "")}
                            title={media.title || `Video ${index + 1}`}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        )}
                      </MediaItem>
                    ))}

                    {mediaItems.length > 1 && (
                      <>
                        <NavigationButton
                          direction="left"
                          onClick={(e) => {
                            e.stopPropagation();
                            goToPrevious();
                          }}
                        >
                          <ChevronLeft size={16} color="#64748b" />
                        </NavigationButton>
                        <NavigationButton
                          direction="right"
                          onClick={(e) => {
                            e.stopPropagation();
                            goToNext();
                          }}
                        >
                          <ChevronRight size={16} color="#64748b" />
                        </NavigationButton>
                      </>
                    )}
                  </MediaContainer>

                  {mediaItems.length > 1 && (
                    <MediaIndicators>
                      {mediaItems.map((_, index) => (
                        <Indicator
                          key={index}
                          isActive={index === currentMediaIndex}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentMediaIndex(index);
                          }}
                          aria-label={`Go to slide ${index + 1}`}
                        />
                      ))}
                    </MediaIndicators>
                  )}

                  {currentMedia && (
                    <MediaInfo>
                      {currentMedia.title && (
                        <MediaTitle>{currentMedia.title}</MediaTitle>
                      )}
                      {currentMedia.description && (
                        <MediaDescription>
                          {currentMedia.description}
                        </MediaDescription>
                      )}
                    </MediaInfo>
                  )}
                </MediaGalleryContainer>
              </DetailsRightColumn>
            )}
            <DetailsLeftColumn>
              <DetailItem>
                <DetailLabel>Title</DetailLabel>
                <DetailValue>{fundraiser.title}</DetailValue>
              </DetailItem>

              {fundraiser.subtitle && (
                <DetailItem>
                  <DetailLabel>Sub-Title</DetailLabel>
                  <DetailValue>{fundraiser.subtitle}</DetailValue>
                </DetailItem>
              )}

              <DetailItem>
                <DetailLabel>Date Range</DetailLabel>
                <DateRangeContainer>
                  <DetailValue>{getDateRangeDisplay()}</DetailValue>
                  {/* Badge to the right of date range */}
                  {shouldShowPendingBadgeInDetails && (
                    <PendingBadge>
                      {getPendingBadgeText(new Date(fundraiser.startDate))}
                    </PendingBadge>
                  )}
                  {shouldShowExpirationBadgeInDetails && (
                    <ExpirationBadge>
                      {getExpirationBadgeText(
                        fundraiser.endDate
                          ? new Date(fundraiser.endDate)
                          : new Date()
                      )}
                    </ExpirationBadge>
                  )}
                </DateRangeContainer>
              </DetailItem>

              <DetailItem>
                <DetailLabel>Category</DetailLabel>
                <DetailValue>{fundraiser.category?.name || ""}</DetailValue>
              </DetailItem>
            </DetailsLeftColumn>
          </DetailsGrid>
        </DetailsContent>
      </DetailsContainer>
    );
  });

export default withRouter(FundraiserDetailsSection);
